import { useEffect, useRef, useState } from 'react'
import TitleHeader from '../TitleHeader/TitleHeader'
import rockett from '../../../assets/images/developmentlanguage/rockett.webp'
import Constants from '../../../app/utils/constants'

const databaseIcons = Constants.databaseIcons

const databaseLinks: Record<string, string> = {
  MySQL: 'https://www.mysql.com',
  MongoDB: 'https://www.mongodb.com',
  Redis: 'https://redis.io',
  Firebase: 'https://firebase.google.com',
}

const cellClassByIndex = [
  'col-start-1 row-start-1 justify-start',
  'col-start-1 row-start-2 justify-start',
  'col-start-2 row-start-1 justify-start',
  'col-start-2 row-start-2 justify-start',
] as const

const rowOfIndex = [0, 1, 0, 1]
const directionOfIndex = ['left', 'left', 'right', 'right']

const DatabaseService = () => {
  // ── Cursor glow (scroll-aware) ────────────────────────────────────────────
  const containerRef = useRef<HTMLElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  useEffect(() => {
    let clientX = 0, clientY = 0, currentX = 0, currentY = 0, rafId = 0
    let inside = false

    const container = containerRef.current
    if (!container) return

    const getRelativePos = () => {
      const rect = container.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    }

    const setPos = (x: number, y: number) => {
      if (blobRef.current)
        blobRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
    }

    const handleMouseMove = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
    }

    const handleEnter = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
      inside = true
      setIsInside(true)
    }

    const handleLeave = () => {
      inside = false
      setIsInside(false)
    }

    const handleScroll = () => {
      if (!inside) return
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
    }

    const animate = () => {
      if (inside) {
        const { x, y } = getRelativePos()
        currentX += (x - currentX) * 0.1
        currentY += (y - currentY) * 0.1
        setPos(currentX, currentY)
      }
      rafId = requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafId = requestAnimationFrame(animate)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  // ── Per-row visibility ────────────────────────────────────────────────────
  const row0Ref = useRef<HTMLDivElement>(null)
  const row1Ref = useRef<HTMLDivElement>(null)
  const [row0Visible, setRow0Visible] = useState(false)
  const [row1Visible, setRow1Visible] = useState(false)

  useEffect(() => {
    const makeObserver = (el: HTMLDivElement | null, setter: (v: boolean) => void) => {
      if (!el) return
      const obs = new IntersectionObserver(([entry]) => setter(entry.isIntersecting), { threshold: 0.2 })
      obs.observe(el)
      return obs
    }
    const o0 = makeObserver(row0Ref.current, setRow0Visible)
    const o1 = makeObserver(row1Ref.current, setRow1Visible)
    return () => { o0?.disconnect(); o1?.disconnect() }
  }, [])

  const rowVisible = [row0Visible, row1Visible]

  // ── Lines visibility ──────────────────────────────────────────────────────
  const linesRef = useRef<HTMLDivElement>(null)
  const [linesVisible, setLinesVisible] = useState(false)

  useEffect(() => {
    if (!linesRef.current) return
    const obs = new IntersectionObserver(([entry]) => setLinesVisible(entry.isIntersecting), { threshold: 0.15 })
    obs.observe(linesRef.current)
    return () => obs.disconnect()
  }, [])

  // ── Bottom banner visibility ──────────────────────────────────────────────
  const bottomRef = useRef<HTMLDivElement>(null)
  const [bottomVisible, setBottomVisible] = useState(false)

  useEffect(() => {
    if (!bottomRef.current) return
    const obs = new IntersectionObserver(([entry]) => setBottomVisible(entry.isIntersecting), { threshold: 0.15 })
    obs.observe(bottomRef.current)
    return () => obs.disconnect()
  }, [])

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden py-12 px-[8%] md:py-16"
    >
      {/* Cursor glow blob */}
      <div
        ref={blobRef}
        className={`pointer-events-none absolute w-[650px] h-[650px] rounded-full
          bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)]
          blur-3xl z-0 transition-opacity duration-300 ease-out
          ${isInside ? 'opacity-50' : 'opacity-0'}`}
      />

      <style>{`
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-7px); }
        }
        .icon-float { animation: floatIcon 3s ease-in-out infinite; }

        .slide-left {
          transform: translateX(-170px);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }
        .slide-right {
          transform: translateX(170px);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }
        .slide-in {
          transform: translateX(0);
          opacity: 1;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }

        .line-hidden {
          transform: translate(-50%, -50%) scale(0);
          opacity: 0;
          transition: transform 0.85s ease-in, opacity 0.6s ease-in;
        }
        .line-show {
          transform: translate(-50%, -50%) scale(1);
          opacity: 1;
          transition: transform 0.85s ease-in, opacity 0.6s ease-in;
        }

        .banner-hidden {
          transform: scale(0.82);
          opacity: 0;
          transition: transform 0.8s ease-in, opacity 0.65s ease-in;
        }
        .banner-show {
          transform: scale(1);
          opacity: 1;
          transition: transform 0.8s ease-in, opacity 0.65s ease-in;
        }
      `}</style>

      {/* Header */}
      <div className="relative z-10 mb-8 flex justify-center md:mb-10">
        <TitleHeader
          badgeText="Software we are using"
          titleLines={['Our Database Services']}
          wrapperClassName="flex flex-col items-center text-center"
          titleClassName="font-rajdhani text-3xl font-semibold md:text-5xl lg:text-4xl xl:text-4xl 2xl:text-6xl"
          badgeClassName="px-8 py-2 text-sm font-rajdhani font-semibold uppercase md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
        />
      </div>

      {/* Grid */}
      <div className="relative z-10 mb-12">
        <div
          ref={linesRef}
          className="relative mx-auto grid w-full grid-cols-2 grid-rows-2 gap-x-6 gap-y-8 px-2 py-2
            sm:gap-x-8 sm:gap-y-10 sm:px-3 sm:py-3
            md:max-w-[760px] md:gap-x-10 md:gap-y-12 md:px-4 md:py-4
            lg:max-w-[960px] xl:max-w-[1160px] 2xl:max-w-[1060px]"
        >
          <div aria-hidden className={`pointer-events-none absolute left-1/2 top-1/2 h-full w-[2px]
            bg-[linear-gradient(180deg,transparent_0%,#EA892A_18%,#F0578B_82%,transparent_100%)]
            ${linesVisible ? 'line-show' : 'line-hidden'}`} />

          <div aria-hidden className={`pointer-events-none absolute left-1/2 top-1/2 h-[2px] w-full
            bg-[linear-gradient(90deg,transparent_0%,#EA892A_12%,#F0578B_88%,transparent_100%)]
            ${linesVisible ? 'line-show' : 'line-hidden'}`} />

          <div ref={row0Ref} aria-hidden className="pointer-events-none absolute top-0 left-0 w-full h-[50%]" />
          <div ref={row1Ref} aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-full h-[50%]" />

          {databaseIcons.map((item, index) => {
            const myRow = rowOfIndex[index]
            const isVisible = rowVisible[myRow]
            const dir = directionOfIndex[index]

            return (
              <div
                key={item.name}
                className={`flex lg:pl-20 w-full min-w-0 items-center ${cellClassByIndex[index]}
                  ${isVisible ? 'slide-in' : dir === 'left' ? 'slide-left' : 'slide-right'}`}
                style={{ transitionDelay: isVisible ? `${(index % 2) * 0.12}s` : '0s' }}
              >
                <a
                  href={databaseLinks[item.name] ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 items-center gap-x-3 gap-y-2 p-3 sm:gap-x-4 sm:p-4 md:gap-x-5 md:p-5 group cursor-pointer"
                >
                  <div className="shrink-0 rounded-full p-[1.25px] bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]
                    transition-transform duration-300 group-hover:scale-110">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0E0924]
                      sm:h-18 sm:w-18 md:h-20 md:w-20 lg:h-22 lg:w-22 xl:h-26 xl:w-26 2xl:h-30 2xl:w-30">
                      <img src={item.icon} alt={item.name}
                        className="icon-float h-5 w-5 object-contain sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 xl:h-14 xl:w-14 2xl:h-16 2xl:w-16" />
                    </div>
                  </div>
                  <span className="font-rajdhani text-base font-semibold leading-tight text-white
                    sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl 2xl:text-4xl
                    transition-colors duration-300 group-hover:text-purple-300 underline-offset-4 group-hover:underline">
                    {item.name}
                  </span>
                </a>
              </div>
            )
          })}
        </div>
      </div>

      {/* Bottom banner */}
      <div
        ref={bottomRef}
        className={`relative z-10 left-1/2 w-screen -translate-x-1/2 overflow-hidden bg-purple origin-center
          ${bottomVisible ? 'banner-show' : 'banner-hidden'}`}
      >
        <div className="mx-auto grid w-full grid-cols-1 gap-y-8 px-[5%]
          md:grid-cols-[minmax(0,1.95fr)_minmax(0,0.80fr)] md:gap-y-0">
          <div className="flex h-full w-full flex-col justify-center py-6">
            <p className="font-rajdhani leading-relaxed font-semibold text-white
              text-base md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl">
          The stack is only as good as the team that operates it. Our engineers have built production systems for fintech and healthcare clients across the US, UK, and India, where failure isn't an option.
            </p>
          </div>
          <div className="hidden md:flex w-full h-full items-end justify-end">
  <img
    src={rockett}
    alt="Database services visual"
    className="w-full h-[90%] object-contain"
  />
</div>
        </div>
      </div>
    </section>
  )
}

export default DatabaseService